import { useKaraoke } from '@src/hooks/useKaraoke';
import { useEditor, useKaraokeStore } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import {
  Bold,
  BoldValues,
  findClosestBoldValueFont,
  getBoldValuesFromCustomFonts,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import CircleCheckIcon from '../../svgs/CircleCheckIcon';
import { KaraokeCard } from '../../types.ts/general';
import SaveOrCancelTemplate from '../common/SaveOrCancelTemplate';
import TextBrandingTemplates from '../common/TextBrandingTemplates';
import SpinningLoading from '../SpinningLoading';
import GlobalKaraokePresetSelect from './GlobalKaraokePresetSelect';
import KaraokeTemplateSelect from './KaraokeTemplateSelect';
import {
  Action,
  AlignValues,
  Readability,
  TextSettingsUI,
} from './TextSettingsUI';
import TextTranscriptionSettingsUI from './TextTranscriptionSettingsUI';

import type { Align } from './TextSettingsUI';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { ElementState } from '@src/renderer/ElementState';

type Props = {
  activeElement: ElementState | undefined;
  fallbackKaraokeConfig: KaraokeConfig;
};

/**
 * KaraokeEditorView Component
 *
 * Main karaoke editing interface with clearly separated dependencies for easier refactoring.
 *
 * DEPENDENCIES:
 * =============
 * 1. videoCreator (VideoCreatorStore) - Main store instance
 *    - isKaraokeGenerated: boolean
 *    - karaokeSubMenu: KaraokeCard enum
 *    - karaokeLoading: boolean
 *    - isTimelineLoading: boolean
 *    - renderer: object
 *    - story: object
 *    - currentVideo: object
 *    - organization: object
 *    - setShowSubtitles(boolean): void
 *
 * 2. videoCreator.karaokeProducer - Karaoke generation service
 *    - getKaraokeConfig(): KaraokeConfig
 *    - getKaraokeTextSettingByAspectRatio(): Partial<KaraokeConfig>
 *    - rerenderWithNewConfig(config: KaraokeConfig): void
 *    - produceKaraoke(config: KaraokeConfig): Promise<void>
 *    - getClipByText(text: string): Clip | undefined
 *    - getFullyUpdatedVariablePositionings(layoutType, field, value): object | undefined
 *    - getVariablePositioningsFor(layoutType): object | undefined
 *
 * 3. videoCreator.textBrandingService - Template management service
 *    - isTemplateModified: boolean
 *    - selectedTemplateId: string | undefined
 *    - brandingTemplates: Template[]
 *    - isLoading: boolean
 *    - isPropsChanged: boolean
 *    - prepareTemplateToSave: boolean
 *    - checkPropsChanged(type: string, props: object): void
 *    - saveToExtraElementData(type: string, templateId: string): void
 *    - getAllBrandingTemplates(type: string, aspectRatio: string, hasActiveElement: boolean): void
 *    - isPresetTemplate(templateId: string | undefined): boolean
 *
 * STATE:
 * ======
 * - addNewTemplate: boolean
 * - templateName: string | undefined
 * - templateNameError: boolean
 * - allowCustomTemplateSave: boolean
 * - selectedActions: (Action | Readability)[]
 */
const KaraokeEditorView = observer(
  forwardRef<HTMLDivElement, Props>(
    ({ activeElement, fallbackKaraokeConfig }, ref) => {
      // ========================================
      // DEPENDENCIES
      // ========================================
      const videoCreator = useVideoCreatorStore();
      const editorStore = useEditor();
      const karaokeProducer = videoCreator.karaokeProducer;
      const textBrandingService = videoCreator.textBrandingService;
      const karaokeStore = useKaraokeStore();
      const {
        getKaraokeConfigForKaraokeClipId,
        setDefaultKaraokeConfigId,
        updateKaraokeConfigAndProduce,
        commandProduceKaraoke,
      } = useKaraoke(editorStore.editor);

      // ========================================
      // DERIVED STATE FROM DEPENDENCIES
      // ========================================
      const isKaraokeGenerated = karaokeStore.isKaraokeGenerated;

      const activeKaraokeElement = activeElement
        ? karaokeStore.karaokeLines.find(
            line => line.id === activeElement.source.id,
          )
        : null;

      const config =
        (activeKaraokeElement
          ? getKaraokeConfigForKaraokeClipId(activeKaraokeElement.id)
          : karaokeStore.defaultKaraokeConfig) ?? fallbackKaraokeConfig;

      const brandCustomFonts =
        videoCreator.organization?.branding?.customFonts || [];

      // ========================================
      // LOCAL STATE
      // ========================================
      const [addNewTemplate, toggleAddNewTemplate] = useState<boolean>(false);
      const [templateName, setTemplateName] = useState<string>();
      const [templateNameError, setTemplateNameError] =
        useState<boolean>(false);
      const [allowCustomTemplateSave, setAllowCustomTemplateSave] =
        useState<boolean>(false);
      const [selectedActions, setSelectedActions] = useState<
        (Action | Readability)[]
      >([Action.align]);

      // ========================================
      // COMPUTED VALUES
      // ========================================
      const selectedShadow = useMemo(
        () =>
          config?.shadow_color
            ? {
                color: config.shadow_color as string,
                blur: config.shadow_blur as number,
                x: config.shadow_x as number,
                y: config.shadow_y as number,
              }
            : undefined,
        [config],
      );

      const hasStickyBottom =
        addNewTemplate ||
        allowCustomTemplateSave ||
        textBrandingService.isTemplateModified;

      const fontFamily = config.font_family;
      const fontWeight = config.font_weight;

      const selectedBold = useMemo(() => {
        const customFont = getCustomFontFromPostScriptName(
          fontFamily,
          brandCustomFonts,
        );
        if (customFont) return customFont.fontSubfamily;
        return Object.keys(BoldValues).find(
          (k: any) => BoldValues[k as Bold] === Number(fontWeight),
        ) as Bold;
      }, [brandCustomFonts, fontFamily, fontWeight]);

      const templates = (textBrandingService.brandingTemplates || []).filter(
        t => {
          const templateId = typeof t.id === 'string' ? t.id : undefined;
          return !textBrandingService.isPresetTemplate(templateId);
        },
      );

      // ========================================
      // HELPER FUNCTIONS
      // ========================================
      function updateSelectedActionsForReadability(
        newConfig: any,
        selectedActions: (Action | Readability)[],
      ) {
        for (const action in Readability) {
          if (Object.prototype.hasOwnProperty.call(Readability, action)) {
            const readabilityAction =
              Readability[action as keyof typeof Readability];

            if (
              newConfig[`hide${readabilityAction}` as keyof typeof newConfig] &&
              !selectedActions.includes(readabilityAction)
            ) {
              selectedActions.push(readabilityAction);
            } else if (
              !newConfig[
                `hide${readabilityAction}` as keyof typeof newConfig
              ] &&
              selectedActions.includes(readabilityAction)
            ) {
              selectedActions = selectedActions.filter(
                action => action !== readabilityAction,
              );
            }
          }
        }
      }

      // ========================================
      // CORE ACTIONS (DEPENDENCIES INTERACTION)
      // ========================================
      const modifyProperty = async (propertyMap: Partial<KaraokeConfig>) => {
        const newConfig = { ...config, ...propertyMap };

        textBrandingService.checkPropsChanged('karaoke', propertyMap);
        updateKaraokeConfigAndProduce(newConfig);
      };

      const setAnimation = (
        time: string | number | null,
        type: any,
        duration: number | null = null,
        background_effect: string | null = null,
      ) => {
        const isInstagram = type === 'instagram';
        // Remove existing animation from list
        const newAnimations =
          config.animations?.filter((keyframe: any) =>
            [0, 'start'].includes(time!)
              ? ![0, 'start'].includes(keyframe.time)
              : keyframe.time !== time,
          ) ?? [];

        if (type !== 'none') {
          if (isInstagram) {
            type = 'text-appear';
          }
          const animation: any = { type };

          if (time) {
            animation.time = time;
            animation.duration = duration || 1;
          }

          if (background_effect) {
            animation.background_effect = background_effect;
          }

          // Reverse animation when used as exit animation
          if (time === 'end') {
            animation.reversed = true;
          }

          newAnimations.push(animation);
        }
        const newConfig: KaraokeConfig = {
          ...config,
          // TODO instagram effect support needed?
          // instagramEffect: isInstagram,
          // ...(!config.instagramEffect &&
          //   isInstagram && {
          //     hideComma: true,
          //     hidePeriod: true,
          //   }),
          language: isInstagram ? 'original' : config.language,
          animations: newAnimations,
        };
        textBrandingService.checkPropsChanged('karaoke', {
          animations: newAnimations,
        });
        updateKaraokeConfigAndProduce(newConfig);
      };

      const produceKaraokeText = async (newConfig?: KaraokeConfig) => {
        analytics.track('karaoke_text_generated', {
          config: newConfig,
          storyId: videoCreator.story?.id,
          storyTitle: videoCreator.story?.title,
          videoId: videoCreator.currentVideo?.id,
          videoTitle: videoCreator.currentVideo?.title,
        });

        karaokeStore.setIsKaraokeLoading(true);
        try {
          if (newConfig) {
            setDefaultKaraokeConfigId(newConfig.id);
            updateKaraokeConfigAndProduce(newConfig);
          } else {
            newConfig = {
              ...config,
              ...(!isKaraokeGenerated &&
                karaokeProducer.getKaraokeTextSettingByAspectRatio()),
            };
            setDefaultKaraokeConfigId(newConfig.id);
            commandProduceKaraoke();
          }
          // TODO continue refactoring from here
          videoCreator.setShowSubtitles(true);

          // Save karaokeTemplateId to extraElementData when producing karaoke
          const templateId =
            textBrandingService.selectedTemplateId || 'default';
          textBrandingService.saveToExtraElementData('karaoke', templateId);
          if (!isKaraokeGenerated && !textBrandingService.selectedTemplateId) {
            textBrandingService.selectedTemplateId = 'default';
          }
        } catch (e) {
          console.error('Error generating karaoke text:', e);
        } finally {
          videoCreator.karaokeLoading = false;
        }
      };

      // ========================================
      // UI ACTION HANDLERS
      // ========================================
      const toggleAction = (action: Action) => {
        const hasAction = selectedActions.find(a => a === action);
        let updatedActions = [...selectedActions];
        if (hasAction) {
          updatedActions = updatedActions.filter(a => a !== action);
        } else {
          updatedActions = [...updatedActions, action];
        }
        setSelectedActions(updatedActions);
      };

      const handleToggleAction = (
        action: Action,
        propertyName: keyof KaraokeConfig,
        normalState: string,
        requiredState: string,
      ) => {
        toggleAction(action);
        const isSelected = selectedActions.includes(action);
        const state = isSelected ? normalState : requiredState;
        modifyProperty({ [propertyName]: state });
      };

      const handleSanitizePunctuation = async (action: Readability) => {
        const newConfig = { ...config };
        if (action === Readability.comma) {
          newConfig.hideComma = !config.hideComma;
        }
        if (action === Readability.period) {
          newConfig.hidePeriod = !config.hidePeriod;
        }
        if (action === Readability.filler) {
          newConfig.hideFillers = !config.hideFillers;
        }
        await produceKaraokeText(newConfig);
      };

      const toggleAlign = (align: Align) => {
        const modifications = {
          x_alignment: AlignValues[align],
          x: '50%', // default position to keep it visible
        };
        modifyProperty(modifications);
      };

      const handleSetBold = (
        bold: string,
        fontWeight: number,
        fontScriptName?: string,
      ) => {
        const state = selectedBold === bold ? null : bold;
        const value = state ? fontWeight : BoldValues[Bold.normal];
        modifyProperty({
          font_weight: value.toString(),
          ...(fontScriptName && { font_family: fontScriptName }),
        });
      };

      const handleSetFont = (fontScriptName?: string) => {
        let _fontWeight = config.font_weight;
        if (fontScriptName) {
          const isCustomFont = !!getCustomFontFromPostScriptName(
            fontScriptName,
            brandCustomFonts,
          );
          if (isCustomFont) {
            const { fontsByWeight } = getBoldValuesFromCustomFonts(
              fontScriptName,
              brandCustomFonts,
            );
            const closestBold = findClosestBoldValueFont(fontsByWeight);
            if (closestBold) {
              _fontWeight = closestBold.weight.toString();
              fontScriptName = closestBold.font.postScriptName;
            }
          } else {
            _fontWeight = BoldValues[Bold.normal].toString();
          }
        }
        modifyProperty({
          font_family: fontScriptName,
          font_weight: _fontWeight,
        });
      };

      const handleAddTemplateButtonClick = async () => {
        if (!isKaraokeGenerated) {
          await produceKaraokeText();
        }
        textBrandingService.isTemplateModified = false;
        textBrandingService.prepareTemplateToSave = true;
        videoCreator.karaokeSubMenu = KaraokeCard.styles;
        toggleAddNewTemplate(true);
        setAllowCustomTemplateSave(true);
      };

      // ========================================
      // EFFECTS
      // ========================================
      useEffect(() => {
        const selectedActions: (Action | Readability)[] = [];
        if (config.font_style === 'italic') {
          selectedActions.push(Action.italic);
        }
        if (config.text_transform === 'uppercase') {
          selectedActions.push(Action.capitalize);
        }
        updateSelectedActionsForReadability(config, selectedActions);
        setSelectedActions(selectedActions);
      }, [config]);

      useEffect(() => {
        return () => {
          if (textBrandingService.isTemplateModified) {
            textBrandingService.isTemplateModified = false;
          }
        };
      }, []);

      // Ensure karaoke templates are loaded when navigating to the Templates tab
      useEffect(() => {
        if (videoCreator.karaokeSubMenu !== KaraokeCard.templates) return;
        if (
          textBrandingService.brandingTemplates.length === 0 &&
          !textBrandingService.isLoading
        ) {
          const aspectRatio =
            videoCreator.currentVideo?.aspectRatio ||
            videoCreator.getDefaultAspectRatio();
          const hasActiveElement = !!activeElement;
          textBrandingService.getAllBrandingTemplates(
            'karaoke',
            aspectRatio,
            hasActiveElement,
          );
        }
      }, [
        videoCreator.karaokeSubMenu,
        videoCreator.currentVideo?.aspectRatio,
        activeElement,
        textBrandingService,
      ]);

      // ========================================
      // RENDER HELPERS
      // ========================================
      const alignType = Object.keys(AlignValues).find(
        k => AlignValues[k as Align] === config.x_alignment,
      ) as Align;

      const renderAnimationsTabContent = () =>
        isKaraokeGenerated && (
          <TextTranscriptionSettingsUI
            karaokeElement={config}
            modifyProperty={patch =>
              modifyProperty(patch as Record<string, unknown>)
            }
          />
        );

      const renderStylesTabContent = () =>
        isKaraokeGenerated && (
          <TextSettingsUI
            ref={ref}
            skipFields={['text']}
            karaokeElement={config}
            selectedBold={selectedBold}
            selectedFontWeight={fontWeight}
            selectedActions={selectedActions}
            selectedFontFamily={fontFamily}
            selectedFontSize={config.font_size}
            selectedFillColor={config.fill_color}
            selectedBackgroundColor={config.background_color}
            selectedBackgroundAlignThreshold={config.background_align_threshold}
            selectedBorderRadius={config.background_border_radius}
            selectedShadow={selectedShadow}
            modifyProperty={modifyProperty}
            handleSetFont={handleSetFont}
            handleSetBold={handleSetBold}
            handleToggleAction={handleToggleAction}
            handleSanitizePunctuation={handleSanitizePunctuation}
            hasStickyBottom={hasStickyBottom}
            alignType={alignType}
            toggleAlign={toggleAlign}
            setAnimation={setAnimation}
            animations={
              config.animations?.reduce((acc, animation) => {
                if (animation.time === 'start' || animation.time === 0) {
                  acc.enter = animation.type;
                } else if (animation.time === 'end') {
                  acc.exit = animation.type;
                }
                return acc;
              }, {}) || {}
            }
          />
        );

      const renderTemplatesTabContent = () => (
        <>
          <KaraokeTemplateSelect
            onAddTemplateButtonClick={handleAddTemplateButtonClick}
            templates={templates}
            produceKaraokeText={produceKaraokeText}
          />
          <GlobalKaraokePresetSelect produceKaraokeText={produceKaraokeText} />
        </>
      );

      const renderCurrentTabContent = () => {
        switch (videoCreator.karaokeSubMenu) {
          case KaraokeCard.animations:
            return renderAnimationsTabContent();
          case KaraokeCard.templates:
            return renderTemplatesTabContent();
          default:
            return renderStylesTabContent();
        }
      };

      // ========================================
      // MAIN RENDER
      // ========================================
      return (
        <Main>
          {videoCreator.renderer?.ready && (
            <>
              {textBrandingService.isLoading && (
                <SpinningLoading
                  positionTop="20px"
                  text={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Saving template</span>
                    </div>
                  }
                />
              )}
              {textBrandingService.isPropsChanged && (
                <SpinningLoading
                  positionTop="20px"
                  text={
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <span>Creating Custom Template</span>
                    </div>
                  }
                />
              )}
              {isKaraokeGenerated &&
                videoCreator.karaokeSubMenu !== KaraokeCard.templates && (
                  <div style={{ marginBottom: 10 }}>
                    <TextBrandingTemplates
                      ref={ref}
                      hasStickyBottom={hasStickyBottom}
                      addNewTemplate={addNewTemplate}
                      allowCustomTemplateSave={allowCustomTemplateSave}
                      toggleAddNewTemplate={toggleAddNewTemplate}
                      setTemplateNameError={setTemplateNameError}
                      templateNameError={templateNameError}
                      setTemplateName={setTemplateName}
                      hasActiveElement={!!isKaraokeGenerated}
                      type="karaoke"
                    />
                  </div>
                )}
              {renderCurrentTabContent()}
              {isKaraokeGenerated &&
                videoCreator.karaokeSubMenu !== KaraokeCard.templates && (
                  <SaveOrCancelTemplate
                    addNewTemplate={addNewTemplate}
                    setTemplateName={setTemplateName}
                    setTemplateNameError={setTemplateNameError}
                    templateName={templateName}
                    templateNameError={templateNameError}
                    toggleAddNewTemplate={toggleAddNewTemplate}
                    allowCustomTemplateSave={allowCustomTemplateSave}
                    setAllowCustomTemplateSave={setAllowCustomTemplateSave}
                    type="karaoke"
                    setting="karaoke-settings"
                  />
                )}
              {!addNewTemplate &&
                !allowCustomTemplateSave &&
                !textBrandingService.isTemplateModified &&
                videoCreator.karaokeSubMenu !== KaraokeCard.templates && (
                  <GenerateButton onClick={() => produceKaraokeText()}>
                    <CircleCheckIcon strokeColor={'black'} />
                    Generate Karaoke Text NEW
                  </GenerateButton>
                )}
            </>
          )}

          {videoCreator.karaokeLoading &&
            !textBrandingService.isPropsChanged && (
              <SpinningLoading
                positionTop="20px"
                text={
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Generating karaoke text</span>
                  </div>
                }
              />
            )}
          {videoCreator.isTimelineLoading && (
            <SpinningLoading
              positionTop="20px"
              text={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span></span>
                </div>
              }
            />
          )}
        </Main>
      );
    },
  ),
);

export default KaraokeEditorView;

// ========================================
// STYLED COMPONENTS
// ========================================
const Main = styled.div`
  padding-top: 16px;
`;

const GenerateButton = styled.div`
  width: 100%;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: #17c964;
  box-sizing: border-box;

  color: #03041a;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 10px;
`;
